/**
 * Users.
 *
 * Deliberately not an identity platform: create, enable, disable, assign a
 * role. Authentication sits behind an interface on the server so this screen
 * survives a move to Entra ID SSO — at which point creating a user here becomes
 * assigning a role to a directory account, and nothing else on this page
 * changes.
 */
import { useEffect, useState } from 'react';
import { endpoints, type Role, type User } from '../../lib/api';
import {
  Alert,
  Button,
  Card,
  Field,
  Input,
  Select,
  Skeleton,
  formatDateTime,
} from '../../components/ui';
import { AdminTable, StatusPill } from './shared';

export function UsersAdmin({
  currentUserId,
  onError,
}: {
  currentUserId: string;
  onError(message: string): void;
}) {
  const [users, setUsers] = useState<User[] | null>(null);
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = () =>
    endpoints
      .users()
      .then((result) => setUsers(result.users))
      .catch((caught) => onError(String(caught)));

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async (id: string, action: () => Promise<unknown>, message: string) => {
    setBusyId(id);
    setNotice(null);
    try {
      await action();
      await load();
      setNotice(message);
      setCreating(false);
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusyId(null);
    }
  };

  if (!users) return <Skeleton className="h-56 w-full" />;

  return (
    <Card
      title="Users"
      description="Who can sign in, and with what role."
      actions={
        <Button size="sm" onClick={() => setCreating(true)} disabled={creating}>
          Add user
        </Button>
      }
    >
      {notice && (
        <div className="mb-4">
          <Alert tone="success">{notice}</Alert>
        </div>
      )}

      {creating && (
        <UserForm
          busy={busyId === 'new'}
          onCancel={() => setCreating(false)}
          onSubmit={(values) =>
            void run('new', () => endpoints.createUser(values), `${values.email} created.`)
          }
        />
      )}

      <AdminTable
        minWidth={820}
        headers={['Name', 'Email', 'Role', 'Status', 'Last sign-in', 'Actions']}
      >
        {users.map((user) => {
          const isSelf = user.userId === currentUserId;
          return (
            <tr key={user.userId}>
              <td className="px-5 py-3">
                <span className="font-medium text-ink">{user.displayName}</span>
                <span className="block text-[12px] text-muted">{user.jobTitle}</span>
              </td>
              <td className="px-5 py-3 text-muted">{user.email}</td>
              <td className="px-5 py-3">
                <Select
                  aria-label={`Role for ${user.displayName}`}
                  value={user.role}
                  disabled={isSelf || busyId === user.userId}
                  title={isSelf ? 'You cannot change your own role' : undefined}
                  onChange={(event) =>
                    void run(
                      user.userId,
                      () =>
                        endpoints.updateUser(user.userId, {
                          role: event.target.value as Role,
                        }),
                      `${user.email} is now ${event.target.value}.`,
                    )
                  }
                >
                  <option value="SALES">SALES</option>
                  <option value="ADMIN">ADMIN</option>
                </Select>
              </td>
              <td className="px-5 py-3">
                <StatusPill status={user.status} />
              </td>
              <td className="px-5 py-3 text-[12px] text-muted">
                {user.lastLoginAt ? formatDateTime(user.lastLoginAt) : 'Never'}
              </td>
              <td className="px-5 py-3">
                <button
                  type="button"
                  disabled={isSelf || busyId === user.userId}
                  title={isSelf ? 'You cannot disable your own account' : undefined}
                  onClick={() =>
                    void run(
                      user.userId,
                      () =>
                        endpoints.updateUser(user.userId, {
                          status: user.status === 'ACTIVE' ? 'DISABLED' : 'ACTIVE',
                        }),
                      user.status === 'ACTIVE'
                        ? `${user.email} disabled; their sessions were ended.`
                        : `${user.email} enabled.`,
                    )
                  }
                  className="text-[12px] font-semibold text-teal underline disabled:opacity-40"
                >
                  {user.status === 'ACTIVE' ? 'Disable' : 'Enable'}
                </button>
              </td>
            </tr>
          );
        })}
      </AdminTable>

      <p className="mt-4 text-[12px] text-muted">
        Disabling a user ends their sessions immediately. You cannot disable or demote your own
        account, and the last active administrator cannot be removed.
      </p>
    </Card>
  );
}

function UserForm({
  busy,
  onCancel,
  onSubmit,
}: {
  busy: boolean;
  onCancel(): void;
  onSubmit(values: {
    email: string;
    displayName: string;
    jobTitle: string;
    role: Role;
    password: string;
  }): void;
}) {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [role, setRole] = useState<Role>('SALES');
  const [password, setPassword] = useState('');

  return (
    <form
      className="mb-5 space-y-3 rounded-md bg-tint px-4 py-3"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit({ email, displayName, jobTitle, role, password });
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Email" htmlFor="user-email" required>
          <Input
            id="user-email"
            type="email"
            value={email}
            required
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>
        <Field label="Display name" htmlFor="user-name" required>
          <Input
            id="user-name"
            value={displayName}
            required
            maxLength={80}
            onChange={(event) => setDisplayName(event.target.value)}
          />
        </Field>
        <Field
          label="Job title"
          htmlFor="user-title"
          hint="Printed on the cover page when they own a proposal."
        >
          <Input
            id="user-title"
            value={jobTitle}
            maxLength={80}
            onChange={(event) => setJobTitle(event.target.value)}
          />
        </Field>
        <Field label="Role" htmlFor="user-role" required>
          <Select
            id="user-role"
            value={role}
            onChange={(event) => setRole(event.target.value as Role)}
          >
            <option value="SALES">SALES</option>
            <option value="ADMIN">ADMIN</option>
          </Select>
        </Field>
      </div>

      <Field
        label="Initial password"
        htmlFor="user-password"
        required
        hint="At least 12 characters, with upper case, lower case and a digit. The user should change it on first sign-in."
      >
        <Input
          id="user-password"
          type="password"
          value={password}
          required
          minLength={12}
          onChange={(event) => setPassword(event.target.value)}
        />
      </Field>

      <div className="flex gap-3">
        <Button type="submit" size="sm" loading={busy}>
          Create user
        </Button>
        <Button type="button" variant="secondary" size="sm" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
